// Stateless geometry helpers shared between canvas surfaces.
//
// These run against a positions map + a size lookup function — they
// don't read root.canvas.items[] like canvas/functions/canvas/geom.js
// does, so they work for /team-and-agents (which has its own
// `_tagPositions` + per-id PERSON_W/AGENT_W sizing) and any future
// canvas-style surface that doesn't adopt the v2 item state shape.
//
// What goes here vs canvas/geom.js:
//   - sharedGeom.js  : pure (positions, sizes) → result
//   - canvas/geom.js : reads root.canvas.* — tied to canvas v2 state.
// Studios still uses its own inline versions for now; we'll DRY those
// in a follow-up once the shared helpers prove out here.

// Inclusive point-in-rect test. Rect shape: `{ x, y, w, h }`.
export const pointInRect = (x, y, rect) =>
  !!rect &&
  x >= rect.x && x <= rect.x + rect.w &&
  y >= rect.y && y <= rect.y + rect.h

// Walk a set of `{ id, w, h }` candidates and return the first whose
// rect contains the point `(cx, cy)`. `positions[id]` supplies the
// upper-left corner. Skips `ignoreId` so the self-node never matches.
// Returns the matching `id` or null.
export const nodeUnderPoint = ({ candidates, positions, cx, cy, ignoreId = null }) => {
  if (!Array.isArray(candidates) || !positions) return null
  for (const c of candidates) {
    if (!c?.id || c.id === ignoreId) continue
    const p = positions[c.id]
    if (!p) continue
    if (pointInRect(cx, cy, { x: p.x, y: p.y, w: c.w, h: c.h })) return c.id
  }
  return null
}

// Bounding rect around a set of node ids. `sizeOf(id)` returns
// `{ w, h }`. Returns `{ x, y, w, h }` or null when no member resolves.
// Renamed from `groupBbox` to avoid colliding with the existing
// `canvas/functions/canvas/group.js` re-export (which aliases
// `bboxForGroup` and reads root.canvas.items[] — a different surface).
export const bboxOfNodeIds = ({ ids, positions, sizeOf }) => {
  if (!Array.isArray(ids) || !ids.length || !positions || typeof sizeOf !== 'function') return null
  const rects = []
  for (const id of ids) {
    const p = positions[id]
    if (!p) continue
    const { w, h } = sizeOf(id) || {}
    if (!w || !h) continue
    rects.push({ x: p.x, y: p.y, w, h })
  }
  if (!rects.length) return null
  const minX = Math.min(...rects.map((r) => r.x))
  const minY = Math.min(...rects.map((r) => r.y))
  const maxX = Math.max(...rects.map((r) => r.x + r.w))
  const maxY = Math.max(...rects.map((r) => r.y + r.h))
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
}

// Cubic bezier path string between two centers — `M fx fy C fx cy,
// tx cy, tx ty` with control points at the midpoint Y. Mirrors the
// connection-line style /studios uses in buildConnectionsSVG.
export const bezierPath = (fx, fy, tx, ty) => {
  const cy = (fy + ty) / 2
  return `M ${fx} ${fy} C ${fx} ${cy}, ${tx} ${cy}, ${tx} ${ty}`
}

// Convenience — derives the centers from positions + sizeOf for two
// node ids and returns the bezier path. Returns '' when either node
// can't be resolved (saves the caller a null check around _linePath).
export const bezierBetweenNodes = ({ fromId, toId, positions, sizeOf }) => {
  const fp = positions?.[fromId]
  const tp = positions?.[toId]
  if (!fp || !tp) return ''
  const fs = sizeOf(fromId) || {}
  const ts = sizeOf(toId) || {}
  if (!fs.w || !ts.w) return ''
  const fx = fp.x + fs.w / 2
  const fy = fp.y + fs.h / 2
  const tx = tp.x + ts.w / 2
  const ty = tp.y + ts.h / 2
  return bezierPath(fx, fy, tx, ty)
}
